import type { AuthTokens } from '@/types/auth'

const STORAGE_KEY = 'kiro-support-admin-auth'

const isBrowser = typeof window !== 'undefined'

const readFromStorage = (): AuthTokens | null => {
  if (!isBrowser) {
    return null
  }

  const rawValue = window.localStorage.getItem(STORAGE_KEY)

  if (!rawValue) {
    return null
  }

  try {
    const parsed = JSON.parse(rawValue) as AuthTokens
    return parsed
  } catch {
    window.localStorage.removeItem(STORAGE_KEY)
    return null
  }
}

const writeToStorage = (tokens: AuthTokens) => {
  if (!isBrowser) {
    return
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tokens))
}

const removeFromStorage = () => {
  if (!isBrowser) {
    return
  }

  window.localStorage.removeItem(STORAGE_KEY)
}

const inMemoryTokens: { current: AuthTokens | null } = {
  current: null,
}

export const tokenStorage = {
  get: (): AuthTokens | null => {
    if (inMemoryTokens.current) {
      return inMemoryTokens.current
    }

    const storedTokens = readFromStorage()
    inMemoryTokens.current = storedTokens
    return storedTokens
  },
  set: (tokens: AuthTokens) => {
    inMemoryTokens.current = tokens
    writeToStorage(tokens)
  },
  clear: () => {
    inMemoryTokens.current = null
    removeFromStorage()
  },
  getAccessToken: () => tokenStorage.get()?.accessToken ?? null,
  getRefreshToken: () => tokenStorage.get()?.refreshToken ?? null,
  isAccessTokenExpired: () => {
    const expiresAt = tokenStorage.get()?.expiresAt
    if (!expiresAt) {
      return true
    }

    return Date.now() >= expiresAt - 5_000
  },
}
